/**
 * Exchange Rates Page.
 *
 * Admin interface for recording and viewing currency exchange rates
 * used in international procurement. Includes a currency converter tool.
 */

import { useState, useRef } from "react";
import {
  ArrowRightLeft,
  Plus,
  Trash2,
  Upload,
  Globe,
  Download,
  FileSpreadsheet,
  Check,
  AlertCircle,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { downloadBlob } from "@/utils/downloadBlob";

interface ExchangeRate {
  id: string;
  source_currency: string;
  target_currency: string;
  rate: number;
  effective_date: string;
  created_at: string;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
}

const fmt = (v: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(v);

function ExchangeRatesPage() {
  const qc = useQueryClient();

  const { data: currencies } = useQuery<Currency[]>({
    queryKey: ["currencies"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/exchange-rates/currencies")).data
        .currencies,
  });
  const { data: rates, isLoading } = useQuery<ExchangeRate[]>({
    queryKey: ["exchange-rates"],
    queryFn: async () => (await apiClient.get("/api/v1/exchange-rates")).data,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [source, setSource] = useState("EUR");
  const [target, setTarget] = useState("USD");
  const [rate, setRate] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/exchange-rates", {
          source_currency: source,
          target_currency: target,
          rate: parseFloat(rate),
          effective_date: new Date(date).toISOString(),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exchange-rates"] });
      setShowCreate(false);
      setRate("");
      toast.success("Exchange rate recorded", `${source} to ${target}`);
    },
    onError: () => toast.error("Failed to record exchange rate"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/exchange-rates/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["exchange-rates"] });
      toast.success("Exchange rate deleted");
    },
    onError: () => toast.error("Failed to delete exchange rate"),
  });

  // --- Converter tool ---
  const [convAmount, setConvAmount] = useState("");
  const [convSource, setConvSource] = useState("EUR");
  const [convTarget, setConvTarget] = useState("USD");
  const [convResult, setConvResult] = useState<{
    converted_amount: number;
    rate_used: number;
  } | null>(null);

  const convertMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/exchange-rates/convert", {
          amount: parseFloat(convAmount),
          source_currency: convSource,
          target_currency: convTarget,
        })
      ).data,
    onSuccess: (data) => setConvResult(data),
  });

  // --- CSV Import ---
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    skipped: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return (
        await apiClient.post("/api/v1/exchange-rates/import", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
      ).data;
    },
    onSuccess: (data) => {
      setImportResult(data);
      if (data.imported > 0) {
        qc.invalidateQueries({ queryKey: ["exchange-rates"] });
        toast.success(
          "Import complete",
          `${data.imported} rate${data.imported !== 1 ? "s" : ""} imported`,
        );
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: () => toast.error("Import failed"),
  });

  const downloadTemplate = () => {
    const csv =
      "source_currency,target_currency,rate,effective_date\nUSD,EUR,0.92000000,2024-07-01\nUSD,GBP,0.79000000,2024-07-01\nUSD,MXN,17.15000000,2024-07-01\n";
    downloadBlob(
      new Blob([csv], { type: "text/csv" }),
      "exchange_rates_template.csv",
    );
  };

  // --- Fetch External Rates ---
  const [showFetch, setShowFetch] = useState(false);
  const [fetchBase, setFetchBase] = useState("USD");
  const [fetchTargets, setFetchTargets] = useState<string[]>([]);
  const [fetchDate, setFetchDate] = useState("");
  const [fetchResult, setFetchResult] = useState<{
    fetched: number;
    source: string;
    as_of: string;
  } | null>(null);

  const toggleFetchTarget = (code: string) => {
    setFetchTargets((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const selectAllTargets = () => {
    if (!currencies) return;
    const allCodes = currencies
      .map((c) => c.code)
      .filter((c) => c !== fetchBase);
    setFetchTargets(fetchTargets.length === allCodes.length ? [] : allCodes);
  };

  const fetchMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post("/api/v1/exchange-rates/fetch-external", {
          base_currency: fetchBase,
          target_currencies: fetchTargets.length > 0 ? fetchTargets : undefined,
          rate_date: fetchDate || undefined,
        })
      ).data,
    onSuccess: (data) => {
      setFetchResult(data);
      if (data.fetched > 0) {
        qc.invalidateQueries({ queryKey: ["exchange-rates"] });
        toast.success(
          "Rates fetched",
          `${data.fetched} rate${data.fetched !== 1 ? "s" : ""} from ECB as of ${data.as_of}`,
        );
      }
    },
    onError: () => toast.error("Failed to fetch external rates"),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Exchange Rates
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Record exchange rates for international procurement and sales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowFetch(!showFetch);
              setShowImport(false);
              setShowCreate(false);
            }}
          >
            {showFetch ? (
              "Cancel"
            ) : (
              <>
                <Globe className="h-4 w-4" /> Fetch Online
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowImport(!showImport);
              setShowFetch(false);
              setShowCreate(false);
            }}
          >
            {showImport ? (
              "Cancel"
            ) : (
              <>
                <Upload className="h-4 w-4" /> Import CSV
              </>
            )}
          </Button>
          <Button
            onClick={() => {
              setShowCreate(!showCreate);
              setShowImport(false);
              setShowFetch(false);
            }}
          >
            {showCreate ? (
              "Cancel"
            ) : (
              <>
                <Plus className="h-4 w-4" /> Record Rate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createMutation.mutate();
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-4"
          >
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                From Currency
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
              >
                {currencies?.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                To Currency
              </label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
              >
                {currencies?.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Rate"
              type="number"
              step="0.00000001"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              required
              placeholder="1.08000000"
            />
            <Input
              label="Effective Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
            <div className="sm:col-span-4 flex justify-end">
              <Button
                type="submit"
                size="sm"
                isLoading={createMutation.isPending}
              >
                Save Rate
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* CSV Import */}
      {showImport && (
        <Card>
          <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
            <FileSpreadsheet className="mr-2 inline h-4 w-4" />
            Import Exchange Rates from CSV
          </h3>
          <p className="mb-4 text-xs text-[var(--text-secondary)]">
            Upload a CSV file with columns:{" "}
            <code className="text-[var(--text-tertiary)]">
              source_currency, target_currency, rate, effective_date
            </code>
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImportResult(null);
                  importMutation.mutate(file);
                }
              }}
              className="block text-sm text-[var(--text-secondary)] file:mr-3 file:rounded-[var(--radius-md)] file:border-0 file:bg-brand-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-200 dark:file:bg-brand-900/30 dark:file:text-brand-300"
            />
            <button
              type="button"
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 font-medium"
            >
              <Download className="h-3 w-3" /> Download template
            </button>
          </div>
          {importMutation.isPending && (
            <div className="mt-3 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <LoadingSpinner size="sm" /> Importing…
            </div>
          )}
          {importResult && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-3 text-sm">
                {importResult.imported > 0 && (
                  <span className="flex items-center gap-1 text-[var(--text-success)]">
                    <Check className="h-4 w-4" /> {importResult.imported}{" "}
                    imported
                  </span>
                )}
                {importResult.skipped > 0 && (
                  <span className="flex items-center gap-1 text-[var(--text-danger)]">
                    <AlertCircle className="h-4 w-4" /> {importResult.skipped}{" "}
                    skipped
                  </span>
                )}
              </div>
              {importResult.errors.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-2 text-xs text-[var(--text-danger)]">
                  {importResult.errors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Fetch External Rates */}
      {showFetch && (
        <Card>
          <h3 className="mb-1 text-sm font-medium text-[var(--text-primary)]">
            <Globe className="mr-2 inline h-4 w-4" />
            Fetch Rates from ECB
          </h3>
          <p className="mb-4 text-xs text-[var(--text-secondary)]">
            Pull current or historical exchange rates from the European Central
            Bank.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-end">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Base Currency
              </label>
              <select
                value={fetchBase}
                onChange={(e) => setFetchBase(e.target.value)}
                className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
              >
                {currencies?.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Date (blank = latest)"
              type="date"
              value={fetchDate}
              onChange={(e) => setFetchDate(e.target.value)}
            />
            <Button
              size="sm"
              onClick={() => fetchMutation.mutate()}
              isLoading={fetchMutation.isPending}
            >
              <Globe className="h-4 w-4" /> Fetch Rates
            </Button>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-[var(--text-secondary)]">
                Target Currencies
                {fetchTargets.length > 0 && (
                  <span className="ml-1 text-[var(--text-tertiary)]">
                    ({fetchTargets.length} selected)
                  </span>
                )}
              </label>
              <button
                type="button"
                onClick={selectAllTargets}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium"
              >
                {fetchTargets.length ===
                (currencies?.filter((c) => c.code !== fetchBase).length ?? 0)
                  ? "Deselect all"
                  : "Select all"}
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {currencies
                ?.filter((c) => c.code !== fetchBase)
                .map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => toggleFetchTarget(c.code)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      fetchTargets.includes(c.code)
                        ? "bg-brand-600 text-white"
                        : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
                    }`}
                  >
                    {c.code}
                  </button>
                ))}
            </div>
            {fetchTargets.length === 0 && (
              <p className="mt-1.5 text-[11px] text-[var(--text-tertiary)]">
                Leave empty to fetch all available currencies
              </p>
            )}
          </div>

          {fetchResult && (
            <div className="mt-3 rounded-[var(--radius-md)] bg-[var(--surface-sunken)] p-3 text-sm">
              <p className="flex items-center gap-1 text-[var(--text-success)]">
                <Check className="h-4 w-4" /> Fetched {fetchResult.fetched} rate
                {fetchResult.fetched !== 1 ? "s" : ""} from {fetchResult.source}{" "}
                as of {fetchResult.as_of}
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Converter tool */}
      <Card>
        <h3 className="mb-3 text-sm font-medium text-[var(--text-primary)]">
          <ArrowRightLeft className="mr-2 inline h-4 w-4" />
          Currency Converter
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 items-end">
          <Input
            label="Amount"
            type="number"
            step="0.01"
            value={convAmount}
            onChange={(e) => setConvAmount(e.target.value)}
            placeholder="10000"
          />
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              From
            </label>
            <select
              value={convSource}
              onChange={(e) => setConvSource(e.target.value)}
              className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
            >
              {currencies?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              To
            </label>
            <select
              value={convTarget}
              onChange={(e) => setConvTarget(e.target.value)}
              className="mt-1.5 block w-full h-9 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-glass)] px-3 text-sm"
            >
              {currencies?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}
                </option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            onClick={() => convertMutation.mutate()}
            isLoading={convertMutation.isPending}
            disabled={!convAmount}
          >
            Convert
          </Button>
        </div>
        {convResult && (
          <div className="mt-3 rounded-[var(--radius-lg)] bg-[var(--color-surface-tertiary)] p-3 text-center">
            <p className="text-lg font-bold text-brand-600">
              {fmt(convResult.converted_amount, convTarget)}
            </p>
            <p className="text-xs text-[var(--text-tertiary)]">
              Rate: {convResult.rate_used}
            </p>
          </div>
        )}
      </Card>

      {/* Rates list */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : rates && rates.length > 0 ? (
        <div className="space-y-2 stagger-children">
          {rates.map((r) => (
            <Card key={r.id} padding="sm" className="hover-lift">
              <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-200 text-xs font-bold text-brand-700 dark:bg-brand-900/50 dark:text-brand-300">
                  {r.source_currency}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[var(--text-primary)]">
                      {r.source_currency} → {r.target_currency}
                    </span>
                    <Badge variant="info">{r.rate.toFixed(6)}</Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                    Effective: {new Date(r.effective_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="shrink-0 text-right mr-2">
                  <p className="text-sm text-[var(--text-secondary)]">
                    1 {r.source_currency} = {r.rate.toFixed(4)}{" "}
                    {r.target_currency}
                  </p>
                </div>
                <button
                  onClick={() => deleteMutation.mutate(r.id)}
                  className="shrink-0 rounded-[var(--radius-lg)] border border-[var(--text-danger)]/30 p-2 text-[var(--text-danger)] transition-colors hover:bg-[var(--fill-danger)] hover:text-[var(--text-danger)] dark:border-red-900 dark:hover:bg-red-900/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <ArrowRightLeft />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No exchange rates recorded
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Record rates to track international procurement costs.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default ExchangeRatesPage;
